import type { EnergyFigures, EnergyHistory, EnergyQuery, EnergyResolution } from "@spf/contracts";
import type { ChartRange } from "../components/widgets/MainChartWidget.js";
import { SERIES, type ChartData, type SeriesKey } from "./chart.js";
import type { Ledger, LedgerRow } from "../components/analytics/LedgerTable.js";
import type { Async } from "./useAsync.js";

/**
 * Presentation of the energy the API derived: labels and wording only. Every
 * number here arrives computed, and nothing is added up, averaged or scaled --
 * docs/ARCHITECTURE.md section 2 keeps kWh arithmetic out of `apps/web`, so a
 * second client shows the same figures without reimplementing anything.
 */

export type Coverage = "complete" | "partial" | "none";

export function coverageOf(figures: EnergyFigures): Coverage {
  if (figures.recordedSeconds === 0) return "none";
  return figures.complete ? "complete" : "partial";
}

/** The share of a stretch that was recorded, as a whole percentage, rounded down. */
function recordedPercent(figures: EnergyFigures): number {
  if (figures.expectedSeconds === 0) return 0;
  return Math.floor((figures.recordedSeconds / figures.expectedSeconds) * 100);
}

/**
 * What a reader needs to know about how much of a figure is real. Null for a
 * complete stretch, which needs no caveat. Rounded down so a stretch missing
 * one quarter hour never reads "100% recorded" beside a partial marker.
 */
export function coverageNote(figures: EnergyFigures): string | null {
  switch (coverageOf(figures)) {
    case "complete":
      return null;
    case "none":
      return "No readings were recorded";
    case "partial":
      return `Partial: ${String(recordedPercent(figures))}% recorded`;
  }
}

const LABELS: Record<
  EnergyResolution,
  { short: Intl.DateTimeFormatOptions; long: Intl.DateTimeFormatOptions }
> = {
  day: {
    short: { weekday: "short" },
    long: { weekday: "short", day: "numeric", month: "short" },
  },
  week: {
    short: { day: "numeric", month: "short" },
    long: { day: "numeric", month: "short", year: "numeric" },
  },
  month: {
    // Cut to three letters below: en-GB writes September "Sept", and twelve
    // four-letter labels do not fit under a year of bars at 360px.
    short: { month: "long" },
    long: { month: "long", year: "numeric" },
  },
};

/**
 * A bucket's name, on the site's clock rather than the browser's. A reader in
 * another timezone must see the same days the figures were grouped by.
 */
export function bucketLabel(
  history: Pick<EnergyHistory, "resolution" | "timezone">,
  from: string,
  style: "short" | "long",
): string {
  const format = new Intl.DateTimeFormat("en-GB", {
    ...LABELS[history.resolution][style],
    timeZone: history.timezone,
  });
  const label = format.format(new Date(from));
  if (history.resolution === "month" && style === "short") return label.slice(0, 3);
  return history.resolution === "week" && style === "long" ? `Week of ${label}` : label;
}

export function formatKwh(value: number | null): string {
  if (value === null) return "—";
  return `${value.toLocaleString("en-GB", { maximumFractionDigits: 1 })} kWh`;
}

/** Where a since-records-began figure starts, in words. */
export function recordedSince(history: EnergyHistory): string {
  const first = history.buckets[0];
  if (first === undefined) return "Nothing has been recorded yet.";
  const date = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: history.timezone,
  }).format(new Date(first.from));
  return `Worked out from every reading since ${date}.`;
}

/** The design's three chart ranges, as the periods the API understands. */
export const CHART_QUERIES: Record<ChartRange, EnergyQuery> = {
  "7 days": { resolution: "day", last: 7 },
  "6 months": { resolution: "month", last: 6 },
  "1 year": { resolution: "month", last: 12 },
};

/** The analytics switch's three periods, likewise, and what each one's total is called. */
export const LEDGER_QUERIES = {
  month: { resolution: "day", since: "month" },
  ytd: { resolution: "month", since: "year" },
  lifetime: { resolution: "month", since: "records" },
} satisfies Record<string, EnergyQuery>;

export type LedgerPeriod = keyof typeof LEDGER_QUERIES;

export const LEDGER_TOTALS: Record<LedgerPeriod, string> = {
  month: "This month",
  ytd: "Year to date",
  lifetime: "Since records began",
};

export const TODAY_QUERY: EnergyQuery = { resolution: "day", last: 1 };

/** Each flow the chart draws, from a stretch's figures. */
function flowsOf(figures: EnergyFigures): Record<SeriesKey, number | null> {
  return {
    solar: figures.solarKwh,
    grid: figures.gridImportKwh,
    charge: figures.batteryChargeKwh,
    discharge: figures.batteryDischargeKwh,
    house: figures.loadKwh,
  };
}

/** A bucket in words: its name, every flow, and how much of it was recorded. */
function describe(name: string, figures: EnergyFigures): string {
  if (coverageOf(figures) === "none") return `${name}: no readings`;
  const flows = flowsOf(figures);
  const words = SERIES.map(({ key, label }) => `${label.toLowerCase()} ${formatKwh(flows[key])}`);
  const note = coverageNote(figures);
  return `${name}: ${words.join(", ")}${note === null ? "" : ` (${note.toLowerCase()})`}`;
}

/** One point per bucket for every flow, with the range's totals for the key. */
export function chartData(history: EnergyHistory): ChartData {
  return {
    buckets: history.buckets.map((bucket) => {
      const group = bucketLabel(history, bucket.from, "short");
      return {
        group,
        // A month's initial, below the small breakpoint: twelve of them fit at
        // 360px where twelve three-letter names do not.
        ...(history.resolution === "month" ? { narrow: group.charAt(0) } : {}),
        label: describe(bucketLabel(history, bucket.from, "long"), bucket),
        coverage: coverageOf(bucket),
        values: flowsOf(bucket),
      };
    }),
    totals: flowsOf(history.totals),
    note: coverageNote(history.totals),
  };
}

/** The chart's view of a loading, failed or loaded history. */
export function chartState(state: Async<EnergyHistory>): Async<ChartData> {
  return state.kind === "ready" ? { kind: "ready", value: chartData(state.value) } : state;
}

function ledgerRow(bucket: EnergyFigures, period: string): LedgerRow {
  return {
    period,
    solarKwh: bucket.solarKwh,
    gridKwh: bucket.gridImportKwh,
    deliveredKwh: bucket.loadKwh,
    chargedKwh: bucket.batteryChargeKwh,
    dischargedKwh: bucket.batteryDischargeKwh,
    cost: bucket.gridImportCost,
    note: coverageNote(bucket),
  };
}

/**
 * The period's total, then one row per bucket, newest first so the current
 * day or month sits under the total. The total is the API's, not a sum of the
 * rows, so it answers "how much this year" without anyone adding them up.
 */
export function ledgerOf(history: EnergyHistory, period: LedgerPeriod): Ledger {
  return {
    total: ledgerRow(history.totals, LEDGER_TOTALS[period]),
    rows: history.buckets
      .map((bucket) => ledgerRow(bucket, bucketLabel(history, bucket.from, "long")))
      .reverse(),
  };
}
